/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { type Row as TanstackRow } from '@tanstack/react-table';

import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';

import { useMergeRefs } from '../hooks/use-merge-refs';
import { usePragmaticDragAndDrop } from '../hooks/use-pragmatic-drag-and-drop';
import { isSameTable } from '../utils/table-scoping';

import { Row, type RowProps } from './row';

type RowState =
	| {
			type: 'idle';
	  }
	| { type: 'is-over'; closestEdge: Edge | null }
	| { type: 'dragging' };

const idleState: RowState = { type: 'idle' };
const draggingState: RowState = { type: 'dragging' };
const defaultAllowedEdges: Edge[] = ['bottom'];

const dropIndicatorStyles = css({
	position: 'absolute',
	ins: 1,
	left: 0,
	right: 0,
	bottom: 1,
});

export interface PragmaticRowProps<T extends object> extends RowProps {
	children?: React.ReactNode;
	row: TanstackRow<T>;
	index: number;
	enableRowReorder?: boolean;
	allowedEdges?: Edge[];
}

/**
 * __PragmaticRow__
 *
 * EXPERIMENTAL Row element that uses pragmatic dnd for row reordering.
 */
export const PragmaticRow: React.ForwardRefExoticComponent<Omit<PragmaticRowProps<any>, "ref"> & React.RefAttributes<HTMLTableRowElement>> = forwardRef<HTMLTableRowElement, PragmaticRowProps<any>>(
	<T extends object>(
		{
			children,
			row,
			index,
			enableRowReorder = false,
			allowedEdges = defaultAllowedEdges,
			...rowProps
		}: PragmaticRowProps<T>,
		ref: React.Ref<HTMLTableRowElement>,
	) => {
		const dropTargetRef = useRef<HTMLDivElement | null>(null);
		const rowRef = useRef<HTMLTableRowElement | null>(null);
		const [state, setState] = useState<RowState>(idleState);
		const mergedRef = useMergeRefs(rowRef, ref);

		usePragmaticDragAndDrop({
			enabled: enableRowReorder,
			elementRef: rowRef,
			dropTargetRef,
			allowedEdges,
			getData: () => ({
				type: 'item-row',
				row,
				index,
				id: row.id,
			}),
			canDrop: ({ source }) =>
				source.data.type === 'item-row' &&
				source.data.id !== row.id &&
				isSameTable(source.element, rowRef.current),
			canMonitor: ({ source }) =>
				source.data.type === 'item-row' && isSameTable(source.element, rowRef.current),
			onDragStart: () => {
				setState(draggingState);
			},
			onDragEnter: (edge) => {
				setState({
					type: 'is-over',
					closestEdge: edge,
				});
			},
			onDrag: (closestEdge) => {
				// only update react state if the `closestEdge` changes
				setState((current) => {
					if (current.type !== 'is-over') {
						return current;
					}
					if (current.closestEdge === closestEdge) {
						return current;
					}
					return {
						type: 'is-over',
						closestEdge,
					};
				});
			},
			onDragLeave: () => {
				setState(idleState);
			},
			onDrop: () => {
				setState(idleState);
			},
			onDragEnd: () => {
				setState(idleState);
			},
			dependencies: [row, index],
		});

		return (
			<Row {...rowProps} ref={mergedRef} draggable={enableRowReorder}>
				{children}
				{state.type === 'is-over' && state.closestEdge && enableRowReorder && (
					<td css={dropIndicatorStyles}>
						<DropIndicator edge={state.closestEdge} type="no-terminal" />
					</td>
				)}
			</Row>
		);
	},
);
